import { Hammer } from 'lucide-react';

import { AdminPage } from '@/components/admin/AdminPage';

/**
 * A nav destination whose page has not been built yet.
 *
 * Every item in the sidebar leads somewhere from the moment the shell exists,
 * and each one says which phase builds it. The alternative — a link that 404s,
 * or one greyed out with no explanation — makes the shell look broken while it
 * is in fact finished.
 *
 * design-system.md §7: explain, don't hide. Each of these is replaced by the
 * real page in the phase named on it.
 */
export function PlaceholderPage({
  title,
  phase,
  summary,
}: {
  title: string;
  /** "Phase 4" — what the roadmap calls the phase that builds this. */
  phase: string | null;
  /** One line on what the page will do, so the sidebar is readable as a plan. */
  summary: string;
}) {
  return (
    <AdminPage title={title}>
      <div className="rounded-lg border border-ink-300 bg-white px-6 py-12 text-center shadow-card">
        <div className="mx-auto grid size-12 place-items-center rounded-full bg-honey-50">
          <Hammer aria-hidden="true" className="size-6 text-bark-700" />
        </div>
        <p className="mt-4 text-h3 text-ink-900">Not built yet</p>
        <p className="mx-auto mt-1.5 max-w-md text-body text-ink-500">{summary}</p>
        {/*
          **The unscheduled case looks different on purpose.** "Arrives in
          Phase 3" on a page nothing was going to build is the failure this
          exists to stop — it reads as a commitment, so nobody goes looking. A
          page with no phase says so, in the same quiet voice, and the word
          "scheduled" is the thing a reader can act on.
        */}
        <p className="mt-4 inline-block rounded-full bg-honey-50 px-3 py-1 text-meta text-bark-900">
          {phase === null ? 'Not scheduled yet' : `Arrives in ${phase}`}
        </p>
      </div>
    </AdminPage>
  );
}
